import { useId, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { searchConcerts } from '../domain/search.ts'
import { formatDayShort } from '../domain/time.ts'
import { t } from '../i18n.ts'
import { CONCERTS } from '../program.ts'
import { stageColor } from './stageColor.ts'
import './ConcertSearch.css'

/**
 * Artist search over the whole programme, from the day view.
 *
 * It searches all four days on purpose, even though it sits on top of one of them.
 * "When is Mogwai playing" is the question people actually have, and answering it by
 * paging through four days is exactly the work this removes — so a match on Saturday
 * has to be reachable from Wednesday's page. `CONCERTS` is bundled, so this works in
 * a field with no signal and with nobody signed in.
 *
 * Results appear only while something is typed. An empty field is the resting state
 * of the day view, and a permanently open result panel would push the first concert
 * below the fold for the majority of visits that never search at all.
 */
export function ConcertSearch() {
  const [query, setQuery] = useState('')
  const inputId = useId()

  const trimmed = query.trim()
  const matches = useMemo(() => searchConcerts(CONCERTS, trimmed), [trimmed])

  return (
    <div className="concert-search">
      <label className="sr-only" htmlFor={inputId}>
        {t.search.label}
      </label>

      <div className="concert-search__field">
        <SearchIcon />
        <input
          id={inputId}
          type="search"
          className="concert-search__input"
          placeholder={t.search.placeholder}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          autoComplete="off"
          // The browsers' own corrections are tuned for prose, and an artist name is
          // not prose: left on, iOS turns «Reolô» into «Realm» while it is being typed.
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          enterKeyHint="search"
        />
        {trimmed !== '' && (
          // Safari draws its own clear button inside a search input and no other
          // browser does. This one is ours, so the control is there on Android and
          // on the desktop too, and it is a real 44px target rather than a 14px glyph.
          <button
            type="button"
            className="concert-search__clear"
            aria-label={t.search.clear}
            onClick={() => setQuery('')}
          >
            ✕
          </button>
        )}
      </div>

      {trimmed !== '' && (
        /*
         * A named group of links rather than a `<ul>`. The day list below is a real
         * list, and its tests — and any assistive-tech user counting it — read
         * "22 konserter" off the number of list items. A second list in the same
         * region would silently inflate that count, so the results announce
         * themselves as a group and stay out of the day list's arithmetic.
         */
        <div className="concert-search__results" role="group" aria-label={t.search.results}>
          {matches.length === 0 ? (
            <p className="concert-search__empty">{t.search.empty(trimmed)}</p>
          ) : (
            matches.map((concert) => {
              const { weekday, dayOfMonth } = formatDayShort(concert.day)
              return (
                <Link
                  key={concert.id}
                  to={`/konsert/${concert.id}`}
                  className="concert-search__hit"
                  // Clearing on the way out, rather than on unmount: the field is
                  // cheap to restore and a reader coming back to the day view wants
                  // the schedule, not the search they already acted on.
                  onClick={() => setQuery('')}
                >
                  <span className="concert-search__name">{concert.name}</span>
                  <span className="concert-search__meta">
                    <span className="concert-search__day">
                      {weekday} {dayOfMonth}.
                    </span>
                    <span className="concert-search__time">{concert.start}</span>
                    <span className="concert-search__stage">
                      <span
                        className="concert-search__dot"
                        style={{ background: stageColor(concert.stage) }}
                        aria-hidden="true"
                      />
                      {concert.stage}
                    </span>
                  </span>
                </Link>
              )
            })
          )}
        </div>
      )}
    </div>
  )
}

function SearchIcon() {
  return (
    <svg
      className="concert-search__icon"
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <circle cx="11" cy="11" r="6.5" stroke="currentColor" strokeWidth="1.9" />
      <path d="M16 16l4.5 4.5" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
    </svg>
  )
}
